/**
 * The remote registry: which micro-app containers exist, where each one's remoteEntry.js
 * lives, and which URL paths mount which exposed module.
 *
 * It replaces the build-time `remotes` map. The shell bundle carries no remote URLs at
 * all — one bundle serves every environment, and adding or moving an app is an edit to
 * registry/remotes.<env>.json, not a rebuild.
 *
 * This module is shared by three callers, deliberately with no DOM or Node imports:
 *   - scripts/registry.ts   — `registry:check` in CI and `registry:build` for CloudFront
 *   - server/index.ts       — validates at startup and serves /remotes.json
 *   - src/federation/remoteRegistry.ts — the browser, before it registers anything
 * One set of rules in three places is the point: the CLI and the server REJECT an invalid
 * file, the browser DROPS invalid entries and keeps the rest.
 */

export type RegistryEnv = 'local' | 'develop' | 'production';

export const REGISTRY_ENVS: readonly RegistryEnv[] = ['local', 'develop', 'production'];

export interface RegistryRemote {
  /** Container name — must equal the `name` the remote was built with. */
  name: string;
  /** Absolute URL of the remote's remoteEntry.js. */
  entry: string;
}

export interface RegistryRoute {
  /** Absolute URL path, e.g. `/cii`. Matches the `href` of the app's menu record. */
  path: string;
  /** A `name` from `remotes`. */
  remote: string;
  /** The remote's `exposes` key, e.g. `./CIIDashboard`. */
  module: string;
  /** Header title. Omitted where SimpleLayout's default is fine. */
  title?: string;
  /** The italic line under the breadcrumb. Defaults to the app's menu description. */
  subtitle?: string;
  /** Render without SimpleLayout — for detail pages opened from inside their own app. */
  bare?: boolean;
  /**
   * Wrap in a box of definite height, for apps with their own scroll regions. SimpleLayout
   * is auto-height (the shell ships no Tailwind build of its own), so percentage heights
   * inside it never resolve without this.
   */
  fullHeight?: boolean;
  /**
   * Tried when `module` fails to load. The `-detail` routes carry one: their detail expose
   * postdates the apps, and an older deployment of the app does not have it.
   */
  fallbackModule?: string;
}

export interface Registry {
  remotes: RegistryRemote[];
  routes: RegistryRoute[];
}

/**
 * Hosts a remote may be loaded from. In code rather than in the registry, so that whoever
 * edits the registry cannot also widen what it is allowed to point at — the registry
 * decides what code runs inside the shell, with the user's session.
 */
const ALLOWED_HOST_SUFFIXES = ['.siya.com', '.ocean-eye.io'];
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

const MODULE_PATTERN = /^\.\/[A-Za-z0-9_-]+$/;
const NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

export interface ValidateOptions {
  /** Permit `http://localhost` / `127.0.0.1` entries. Only the local registry may. */
  allowLoopback: boolean;
}

export interface ValidationResult {
  /** The entries that passed. A route whose remote was dropped is dropped with it. */
  registry: Registry;
  /** One line per problem, naming the entry and the rule it broke. */
  problems: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function entryProblem(entry: unknown, allowLoopback: boolean): string | null {
  if (typeof entry !== 'string') return 'entry must be a string URL';
  let url: URL;
  try {
    url = new URL(entry);
  } catch {
    return `entry is not an absolute URL: "${entry}"`;
  }
  if (url.username || url.password) return 'entry must not carry credentials';

  if (LOOPBACK_HOSTS.has(url.hostname)) {
    if (!allowLoopback) return `entry host ${url.hostname} is only allowed in the local registry`;
    return url.protocol === 'http:' || url.protocol === 'https:' ? null : 'entry must be http(s)';
  }

  if (url.protocol !== 'https:') return 'entry must use https';
  const allowed = ALLOWED_HOST_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix));
  if (!allowed) {
    return `entry host ${url.hostname} is not allowed (must end in ${ALLOWED_HOST_SUFFIXES.join(' or ')})`;
  }
  return null;
}

/**
 * Validate a parsed registry document.
 *
 * Never throws on bad content — every problem is collected so the CLI can print all of
 * them in one run, and the browser can keep the good entries.
 */
export function validateRegistry(doc: unknown, options: ValidateOptions): ValidationResult {
  const problems: string[] = [];
  const registry: Registry = { remotes: [], routes: [] };

  if (!isRecord(doc)) {
    return { registry, problems: ['registry must be a JSON object with "remotes" and "routes"'] };
  }
  if (!Array.isArray(doc.remotes)) problems.push('"remotes" must be an array');
  if (!Array.isArray(doc.routes)) problems.push('"routes" must be an array');

  const names = new Set<string>();
  for (const [index, raw] of (Array.isArray(doc.remotes) ? doc.remotes : []).entries()) {
    const label = isRecord(raw) && typeof raw.name === 'string' ? `remote "${raw.name}"` : `remotes[${index}]`;
    if (!isRecord(raw)) {
      problems.push(`${label}: must be an object`);
      continue;
    }
    if (typeof raw.name !== 'string' || !NAME_PATTERN.test(raw.name)) {
      problems.push(`${label}: name must match ${NAME_PATTERN}`);
      continue;
    }
    if (names.has(raw.name)) {
      problems.push(`${label}: duplicate name`);
      continue;
    }
    const problem = entryProblem(raw.entry, options.allowLoopback);
    if (problem) {
      problems.push(`${label}: ${problem}`);
      continue;
    }
    names.add(raw.name);
    registry.remotes.push({ name: raw.name, entry: raw.entry as string });
  }

  const paths = new Set<string>();
  for (const [index, raw] of (Array.isArray(doc.routes) ? doc.routes : []).entries()) {
    const label = isRecord(raw) && typeof raw.path === 'string' ? `route "${raw.path}"` : `routes[${index}]`;
    if (!isRecord(raw)) {
      problems.push(`${label}: must be an object`);
      continue;
    }
    const { path, remote, module, title, subtitle, bare, fullHeight, fallbackModule } = raw;

    if (typeof path !== 'string' || !path.startsWith('/') || path.length < 2 || path.endsWith('/')) {
      problems.push(`${label}: path must start with "/" and not end with "/"`);
      continue;
    }
    const key = path.toLowerCase();
    if (paths.has(key)) {
      problems.push(`${label}: duplicate path`);
      continue;
    }
    if (typeof remote !== 'string' || !names.has(remote)) {
      problems.push(`${label}: remote "${String(remote)}" is not a valid entry in "remotes"`);
      continue;
    }
    if (typeof module !== 'string' || !MODULE_PATTERN.test(module)) {
      problems.push(`${label}: module must look like "./Name"`);
      continue;
    }
    if (fallbackModule !== undefined && (typeof fallbackModule !== 'string' || !MODULE_PATTERN.test(fallbackModule))) {
      problems.push(`${label}: fallbackModule must look like "./Name"`);
      continue;
    }
    if ((title !== undefined && typeof title !== 'string') || (subtitle !== undefined && typeof subtitle !== 'string')) {
      problems.push(`${label}: title and subtitle must be strings`);
      continue;
    }
    if ((bare !== undefined && typeof bare !== 'boolean') || (fullHeight !== undefined && typeof fullHeight !== 'boolean')) {
      problems.push(`${label}: bare and fullHeight must be booleans`);
      continue;
    }

    paths.add(key);
    registry.routes.push({
      path,
      remote,
      module,
      ...(title !== undefined ? { title } : {}),
      ...(subtitle !== undefined ? { subtitle } : {}),
      ...(bare ? { bare } : {}),
      ...(fullHeight ? { fullHeight } : {}),
      ...(fallbackModule !== undefined ? { fallbackModule } : {}),
    });
  }

  return { registry, problems };
}

/** The `loadRemote` id for an exposed module: `cii-app` + `./CIIDashboard` → `cii-app/CIIDashboard`. */
export function remoteModuleId(remote: string, module: string): string {
  return `${remote}/${module.replace(/^\.\//, '')}`;
}
